import { useSyncExternalStore } from 'react';

/**
 * Natural (untrimmed) duration of a source video, in seconds, keyed by the
 * media FILE (`editorStore.mediaFileKey`) rather than by the media item.
 *
 * The two are the same id for everything the pipeline generated, and different
 * for the halves of a split — which share one file, and therefore one natural
 * length. Keying by file means the second half's trim handles are clamped
 * correctly the moment it appears, without waiting for its own probe.
 *
 * The backend never serves it, so the timeline learns it from a hidden
 * `<video>`'s `loadedmetadata` event (VideoTrack mounts one probe per video
 * slide). Trim handles clamp against it, so until it lands the handles fall back
 * to the values already in the metadata — never to an unbounded drag.
 *
 * Module-level, like the editor store itself: the cache outlives the blocks
 * that rendered the probes (a reorder remounts them) and survives re-renders.
 */

const durations = new Map<string, number>();
const listeners = new Set<() => void>();
// useSyncExternalStore compares snapshots by identity; a counter gives every
// recorded duration a new snapshot without cloning the map.
let version = 0;

const emit = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getSourceDuration = (fileKey: string): number | undefined => durations.get(fileKey);

/** Records a probed duration. Non-finite/zero readings (a failed load) are ignored. */
export const setSourceDuration = (fileKey: string, seconds: number): void => {
  if (!Number.isFinite(seconds) || seconds <= 0) return;
  if (durations.get(fileKey) === seconds) return;
  durations.set(fileKey, seconds);
  emit();
};

/** Re-renders the caller whenever any probe lands. */
export const useSourceDurationsVersion = (): number =>
  useSyncExternalStore(subscribe, () => version);

export const resetSourceDurations = (): void => {
  durations.clear();
  emit();
};
